import { Box, Button, TextField } from "@mui/material";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { Text } from "@src/appV2/redesign/components/Text";
import { type FormEvent, useState } from "react";

interface LoginWithEmailAndPasswordFormProps {
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  loading: boolean;
  onSubmit: () => Promise<void>;
  onForgotPassword: () => void;
  disabled?: boolean;
  errorMessage?: string;
}

/**
 * Email and password login form
 */
export function LoginWithEmailAndPasswordForm({
  email,
  setEmail,
  password,
  setPassword,
  loading,
  onSubmit,
  onForgotPassword,
  disabled = false,
  errorMessage,
}: LoginWithEmailAndPasswordFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit();
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Box
      component="form"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
    >
      <Text bold variant="body1" sx={{ textAlign: "center" }}>
        Sign in with email and password
      </Text>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <TextField
          fullWidth
          required
          type="email"
          label="Email"
          placeholder="your.email@example.com"
          value={email}
          disabled={disabled || loading}
          autoComplete="email"
          sx={{
            "& .MuiOutlinedInput-root": {
              fontSize: "1rem",
              borderRadius: (theme) => theme.borderRadius?.xLarge,
              backgroundColor: "background.standout",
            },
          }}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />

        <TextField
          fullWidth
          required
          type="password"
          label="Password"
          value={password}
          disabled={disabled || loading}
          autoComplete="current-password"
          sx={{
            "& .MuiOutlinedInput-root": {
              fontSize: "1rem",
              borderRadius: (theme) => theme.borderRadius?.xLarge,
              backgroundColor: "background.standout",
            },
          }}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
        />
        <Button
          variant="text"
          disabled={disabled || loading}
          sx={{
            textTransform: "none",
            fontSize: "0.8rem",
            color: "primary.main",
            alignSelf: "flex-end",
            padding: 0,
            minWidth: "auto",
          }}
          onClick={onForgotPassword}
        >
          Forgot password?
        </Button>
      </Box>

      <LoadingButton
        fullWidth
        type="submit"
        variant="primary"
        size="large"
        isLoading={isSubmitting || loading}
        disabled={disabled}
      >
        Sign In
      </LoadingButton>

      {errorMessage && (
        <Text
          variant="body2"
          role="alert"
          sx={{ color: "error.main", textAlign: "center", marginTop: 2 }}
        >
          {errorMessage}
        </Text>
      )}
    </Box>
  );
}
